import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { createApiClient } from '@/lib/api'
import { installSyncController } from '@/lib/offline/sync-controller'
import { createQueryClient } from '@/lib/query-client'
import { router } from '@/routes'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Application root.
 *
 * Three-step boot:
 *   1. **Hydrate the auth store** from `localStorage`. If a refresh
 *      is needed it happens here, before any route renders. This
 *      avoids the flash-of-login-screen problem when a logged-in
 *      user reloads the dashboard.
 *   2. **Install the sync controller.** Wires `online` / `offline`
 *      events to the IndexedDB queue, kicks off an immediate sync
 *      pass for any rows the previous session left behind, and
 *      keeps the Zustand sync store in sync with reality. The
 *      controller takes its own `ApiClient` (built the same way
 *      `useApi` does it) because we need it before any React
 *      route mounts.
 *   3. **Mount the QueryClient + Router.** Routes that need auth
 *      read the already-hydrated state synchronously in
 *      `beforeLoad`; routes that need offline state read
 *      `useSyncStore` directly.
 *
 * While step 1 is in flight we render a tiny full-screen spinner.
 * The boot is sub-second on a warm cache; the spinner mostly
 * exists for the rare cold-network refresh.
 */
export function App() {
  const queryClient = useMemo(createQueryClient, [])
  const hydrate = useAuthStore((s) => s.hydrate)
  const hydrated = useAuthStore((s) => s.hydrated)
  const [bootError, setBootError] = useState<string | null>(null)

  useEffect(() => {
    void hydrate().catch((err) => {
      // Auth hydration shouldn't *throw* (the store catches all
      // its own errors), but we belt-and-brace just in case so a
      // bug doesn't leave the user staring at a spinner forever.
      setBootError(err instanceof Error ? err.message : String(err))
    })
  }, [hydrate])

  // Install the sync controller exactly once, after hydration.
  // Reading `useAuthStore.getState()` instead of subscribing to
  // it inside the closure means the API client always sees the
  // freshest token without the controller itself re-running on
  // every login/logout.
  useEffect(() => {
    if (!hydrated) return undefined
    const api = createApiClient({
      getAccessToken: () => useAuthStore.getState().accessToken,
      refreshAccessToken: () => useAuthStore.getState().refresh(),
      onAuthFailure: () => useAuthStore.getState().logout(),
    })
    return installSyncController(api)
  }, [hydrated])

  if (!hydrated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-surface-muted text-text-muted">
        <Spinner size={28} label="Chargement de l'application" />
        <p className="text-sm">Chargement…</p>
        {bootError !== null ? (
          <p className="text-xs text-danger px-6 text-center">
            Erreur de démarrage : {bootError}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}
